import {
  Box,
  DropdownButton,
  InputText,
  SelectionList,
  SelectionListChangeEvent,
  Text,
  TextWithHighlight,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import {
  conversationLanguages,
  defaultConversationLanguageCode,
} from 'lib-shared-languages';
import { ampli } from 'lib-shared-tracking';
import { useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { DeepWritable } from 'ts-essentials';

import { conversationsApi } from 'apis/conversations';
import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import { useCommandHandler } from 'utils/commands';
import { useAppSelector } from 'utils/redux/useAppSelector';
import {
  LanguageSelectionListValue,
  useLanguageSelectionList,
  UseLanguageSelectionListOptions,
} from 'utils/useLanguageSelectionList';

import styles from './conversationLanguageSelect.module.scss';

const findLanguageName = (code?: string): string | undefined =>
  conversationLanguages.find((language) => language.code === code)?.name;

export const ConversationLanguageSelect = (): JSX.Element => {
  const hasReplyAccess = useHasAccess('conversation.reply');
  const [updateConversationLanguage] =
    conversationsApi.useUpdateConversationLanguageMutation();

  const [{ id: conversationId, attributes }] = useAppSelector(
    (state) => [state.conversation.metaConversation.conversation],
    shallowEqual,
  );

  const language = attributes?.language;
  const languageName = findLanguageName(language);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useCommandHandler({
    name: 'conversation:language',
    actionFn: () => {
      if (!hasReplyAccess) {
        return;
      }

      setIsOpen((open) => !open);
    },
  });

  const languageSelectionListOptions = useMemo<UseLanguageSelectionListOptions>(
    () => ({
      selectedLanguageCodes: languageName
        ? [language as LanguageSelectionListValue['code']]
        : undefined,
    }),
    [languageName, language],
  );

  const { languages, selectedLanguages, onChange } = useLanguageSelectionList(
    languageSelectionListOptions,
  );

  const filteredLanguages = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return term
      ? languages.filter((item) => item.name.toLowerCase().includes(term))
      : languages;
  }, [languages, searchTerm]);

  const defaultLanguageName = findLanguageName(defaultConversationLanguageCode);

  // When the language is unset or not a recognised conversation language, fall
  // back to displaying the default language.
  const buttonDisplayText = languageName || defaultLanguageName;

  const tooltipText = languageName
    ? 'Conversation language'
    : language
      ? `Language "${language}" is not supported — using the default (${defaultLanguageName})`
      : `No language set — using the default (${defaultLanguageName})`;

  const tooltipContent = hasReplyAccess ? (
    <CommandKeybindingsTooltipContent commandName="conversation:language">
      {tooltipText}
    </CommandKeybindingsTooltipContent>
  ) : (
    'You do not have permission to change the conversation language'
  );

  const handleToggle = (open: boolean) => {
    setIsOpen(open);

    if (open) {
      ampli.conversationCsatMenuOpened();
    } else {
      setSearchTerm('');
    }
  };

  const handleChange = (
    event: SelectionListChangeEvent<LanguageSelectionListValue>,
  ) => {
    if (conversationId == null) {
      return;
    }

    const languageCode = event.selectedItem.value.code;
    onChange(event);

    ampli.conversationCsatMenuLanguageSelected({ languageCode });
    updateConversationLanguage({
      csid: conversationId,
      language: languageCode,
    });

    setIsOpen(false);
  };

  return (
    <Tooltip content={tooltipContent} disabled={isOpen}>
      <DropdownButton
        icon="world"
        show={isOpen}
        onToggle={handleToggle}
        buttonProps={{ variant: 'tertiary', disabled: !hasReplyAccess }}
        dropdownProps={{ autoFocus: true }}
        dropdown={
          <div className={styles.dropdown}>
            <Box padding="300">
              <InputText
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value ?? '')}
                placeholder="Search..."
              />
            </Box>

            <SelectionList<LanguageSelectionListValue>
              className={styles.list}
              isActive={isOpen}
              selectedValues={
                selectedLanguages as DeepWritable<typeof selectedLanguages>
              }
              onChange={handleChange}
            >
              {filteredLanguages.map((item) => (
                <SelectionList.Item
                  key={item.code}
                  value={item}
                  className={styles.listItem}
                >
                  <Text.Paragraph>
                    <TextWithHighlight
                      text={item.name}
                      highlight={searchTerm}
                    />
                  </Text.Paragraph>
                </SelectionList.Item>
              ))}
            </SelectionList>
          </div>
        }
      >
        {buttonDisplayText}
      </DropdownButton>
    </Tooltip>
  );
};
